import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  useApplySuggestedUpdates,
  useDismissSuggestedUpdates,
} from '@/modules/inventory/hooks/useSuggestedUpdates';

/**
 * #1182 / #1179 — the dealer's decision on a flagged value, from the field:
 * `accept` applies a suggestion (the server writes the column and bumps the
 * version); `dismiss` keeps the current value and clears the flag (stage-3 D4).
 * `onReviewed` re-reads the boat and its provenance. A call's own `onDone`
 * runs before it on success; `onFailed` runs after the error toast.
 */
export interface FieldReviewCallbacks {
  onDone?: () => void;
  onFailed?: () => void;
}

export function useFieldReview(boatId: string | null, onReviewed: () => void) {
  const { mutateAsync: applyMutate } = useApplySuggestedUpdates('boats', boatId ?? '');
  const { mutateAsync: dismissMutate } = useDismissSuggestedUpdates('boats', boatId ?? '');
  const onReviewedRef = useRef(onReviewed);
  onReviewedRef.current = onReviewed;

  // `mutateAsync`, not `mutate`: each click settles on its own promise, so a
  // second decision made before the first answers does not drop the first's callbacks.
  const accept = useCallback(
    (fieldSourceIds: string[], callbacks?: FieldReviewCallbacks) => {
      if (!boatId || fieldSourceIds.length === 0) return;
      applyMutate({ field_source_ids: fieldSourceIds }).then(
        (result) => {
          // Part 4G final review m1 — a 200 can report a row skipped (nothing written):
          // treat it as not applied, or the form takes a value the server refused and
          // autosave sends it as the dealer's.
          const skipped = new Set((result?.skipped ?? []).map((row) => row.id));
          if (fieldSourceIds.every((id) => skipped.has(id))) {
            toast.error('This suggestion could not be used. It was left for review.');
            callbacks?.onFailed?.();
            onReviewedRef.current();
            return;
          }
          callbacks?.onDone?.();
          onReviewedRef.current();
        },
        () => {
          toast.error('Could not use the suggestion. Please try again.');
          callbacks?.onFailed?.();
        },
      );
    },
    [applyMutate, boatId],
  );

  const dismiss = useCallback(
    (fieldSourceIds: string[], callbacks?: FieldReviewCallbacks) => {
      if (!boatId || fieldSourceIds.length === 0) return;
      dismissMutate({ field_source_ids: fieldSourceIds }).then(
        () => {
          callbacks?.onDone?.();
          onReviewedRef.current();
        },
        () => {
          toast.error('Could not update the review. Please try again.');
          callbacks?.onFailed?.();
        },
      );
    },
    [dismissMutate, boatId],
  );

  return { accept, dismiss };
}
